import { useEffect, useState, lazy, Suspense } from 'react'
import { Lock, AlertCircle } from 'react-feather'
import { hasToken, resolveCollection, setPasscode, ApiError } from './lib/api'
import type { CollectionView } from './types'
import { Portal } from './components/Portal'
import { Centered, Logo, Spinner } from './components/ui'

// Lazy so the client portal never bundles/loads Firebase — the admin (+ its SDK)
// is a separate chunk fetched only when someone hits /admin.
const Admin = lazy(() => import('./components/admin/Admin').then((m) => ({ default: m.Admin })))

type Status = 'loading' | 'invalid' | 'passcode' | 'ready'

// /admin → the admin dashboard SPA; everything else → the client portal. Match
// the FIRST path segment EXACTLY (not a prefix) so a client/project link like
// /admin-c/<code> or /administrators/<code> still loads the upload portal — only
// the bare /admin console is the admin SPA. The branch is the first thing so the
// admin tree never mounts the client's hooks.
export default function App() {
  const seg = window.location.pathname.replace(/^\/+/, '').split('/')[0]?.toLowerCase()
  if (seg === 'admin') {
    return <Suspense fallback={<Centered><Spinner size={26} /></Centered>}><Admin /></Suspense>
  }
  return <ClientApp />
}

function ClientApp() {
  const [status, setStatus] = useState<Status>('loading')
  const [data, setData] = useState<CollectionView | null>(null)
  const [pc, setPc] = useState('')
  const [pcErr, setPcErr] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    try { setData(await resolveCollection()); setStatus('ready') }
    catch (e) { setStatus((e as ApiError).status === 401 ? 'passcode' : 'invalid') }
  }
  useEffect(() => { if (!hasToken()) { setStatus('invalid'); return } void load() }, [])
  useEffect(() => { if (data?.title) document.title = `${data.title} · Reputifly` }, [data?.title])

  const submitPc = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setPcErr(''); setPasscode(pc.trim())
    try { setData(await resolveCollection()); setStatus('ready') }
    catch (err) { setPcErr((err as ApiError).status === 401 ? 'Incorrect passcode. Try again.' : 'Something went wrong.') }
    finally { setBusy(false) }
  }

  if (status === 'loading') return <Centered><Spinner size={26} /></Centered>

  if (status === 'invalid') return (
    <Centered>
      <div className="card rf-pop-in text-center" style={{ maxWidth: 380, padding: '40px 28px' }}>
        <div className="flex justify-center mb-5"><Logo height={30} /></div>
        <div className="flex justify-center mb-3"><AlertCircle size={30} color="var(--color-faint)" /></div>
        <h1 className="font-semibold text-lg" style={{ color: 'var(--color-ink)' }}>Link unavailable</h1>
        <p className="text-sm mt-1.5" style={{ color: 'var(--color-muted)' }}>
          This link is invalid or has expired. Please ask Reputifly for a fresh link.
        </p>
      </div>
    </Centered>
  )

  if (status === 'passcode') return (
    <Centered>
      <form onSubmit={submitPc} className="card rf-pop-in" style={{ maxWidth: 380, padding: '36px 28px', width: '100%' }}>
        <div className="flex justify-center mb-5"><Logo height={30} /></div>
        <div className="flex justify-center mb-3"><Lock size={26} color="var(--color-primary)" /></div>
        <h1 className="font-semibold text-lg text-center" style={{ color: 'var(--color-ink)' }}>Passcode required</h1>
        <p className="text-sm mt-1 mb-5 text-center" style={{ color: 'var(--color-muted)' }}>Enter the passcode Reputifly shared with you.</p>
        <input className="input" type="password" autoFocus placeholder="Passcode" value={pc} onChange={(e) => setPc(e.target.value)} />
        {pcErr && <p className="text-sm mt-2" style={{ color: 'var(--color-danger)' }}>{pcErr}</p>}
        <button className="btn btn--primary w-full mt-4" disabled={busy || !pc.trim()} type="submit">{busy ? <Spinner size={16} /> : 'Continue'}</button>
      </form>
    </Centered>
  )

  return <Portal initial={data!} />
}
